import { cloneElement, isValidElement, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  registerNavigationGuard,
  unregisterNavigationGuard,
} from "./sidebarNavigation";
import { isEditableElement } from "./builderInputUtils";

/**
 * Tracks unsaved edits per builder section, blocks in-builder sidebar navigation,
 * and blocks main app sidebar / back navigation when the active section is dirty.
 */
export function useSectionDirtyGuard({
  activeSectionId,
  isLoading = false,
  getSaveBeforeLeave,
}) {
  const navigate = useNavigate();
  const dirtySectionsRef = useRef(new Set());
  const suppressDirtyRef = useRef(true);
  const pendingNavigationRef = useRef(null);
  const pendingLeaveActionRef = useRef(null);
  const [pendingSectionId, setPendingSectionId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [dirtyVersion, setDirtyVersion] = useState(0);

  const bumpDirty = useCallback(() => {
    setDirtyVersion((version) => version + 1);
  }, []);

  const isActiveSectionDirty = useCallback(() => {
    return dirtySectionsRef.current.has(activeSectionId);
  }, [activeSectionId, dirtyVersion]);

  useEffect(() => {
    suppressDirtyRef.current = true;
    const timer = window.setTimeout(() => {
      if (!isLoading) {
        suppressDirtyRef.current = false;
      }
    }, 600);
    return () => window.clearTimeout(timer);
  }, [activeSectionId, isLoading]);

  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!dirtySectionsRef.current.has(activeSectionId)) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [activeSectionId, dirtyVersion]);

  useEffect(() => {
    registerNavigationGuard((targetPath) => {
      if (!dirtySectionsRef.current.has(activeSectionId)) return true;
      pendingLeaveActionRef.current = () => navigate(targetPath);
      setLeaveDialogOpen(true);
      return false;
    });
    return () => unregisterNavigationGuard();
  }, [activeSectionId, dirtyVersion, navigate]);

  const markSectionDirty = useCallback(
    (sectionId) => {
      if (suppressDirtyRef.current) return;
      if (!dirtySectionsRef.current.has(sectionId)) {
        dirtySectionsRef.current.add(sectionId);
        bumpDirty();
      }
    },
    [bumpDirty]
  );

  const markActiveSectionDirty = useCallback(() => {
    markSectionDirty(activeSectionId);
  }, [activeSectionId, markSectionDirty]);

  const clearSectionDirty = useCallback(
    (sectionId) => {
      if (dirtySectionsRef.current.delete(sectionId)) {
        bumpDirty();
      }
    },
    [bumpDirty]
  );

  const isSectionDirty = useCallback(
    (sectionId) => dirtySectionsRef.current.has(sectionId),
    [dirtyVersion]
  );

  const requestSectionNavigation = useCallback(
    (targetSectionId, navigateFn) => {
      if (targetSectionId === activeSectionId) {
        navigateFn();
        return;
      }
      if (dirtySectionsRef.current.has(activeSectionId)) {
        pendingNavigationRef.current = navigateFn;
        setPendingSectionId(targetSectionId);
        setDialogOpen(true);
        return;
      }
      navigateFn();
    },
    [activeSectionId]
  );

  const cancelPendingNavigation = useCallback(() => {
    setDialogOpen(false);
    setPendingSectionId(null);
    pendingNavigationRef.current = null;
  }, []);

  const completePendingNavigation = useCallback(() => {
    const navigateFn = pendingNavigationRef.current;
    dirtySectionsRef.current.delete(activeSectionId);
    bumpDirty();
    setDialogOpen(false);
    setPendingSectionId(null);
    pendingNavigationRef.current = null;
    navigateFn?.();
  }, [activeSectionId, bumpDirty]);

  const cancelLeave = useCallback(() => {
    setLeaveDialogOpen(false);
    pendingLeaveActionRef.current = null;
  }, []);

  const completeLeave = useCallback(() => {
    const leaveAction = pendingLeaveActionRef.current;
    dirtySectionsRef.current.delete(activeSectionId);
    bumpDirty();
    setLeaveDialogOpen(false);
    pendingLeaveActionRef.current = null;
    leaveAction?.();
  }, [activeSectionId, bumpDirty]);

  const requestLeave = useCallback(
    (leaveAction) => {
      if (!dirtySectionsRef.current.has(activeSectionId)) {
        leaveAction();
        return;
      }
      pendingLeaveActionRef.current = leaveAction;
      setLeaveDialogOpen(true);
    },
    [activeSectionId, dirtyVersion]
  );

  const confirmLeaveSave = useCallback(async () => {
    const saveFn = getSaveBeforeLeave?.();
    if (saveFn) {
      const saved = await saveFn();
      if (saved !== true) return;
    }
    completeLeave();
  }, [completeLeave, getSaveBeforeLeave]);

  const discardLeave = useCallback(() => {
    const leaveAction = pendingLeaveActionRef.current;
    dirtySectionsRef.current.clear();
    bumpDirty();
    setLeaveDialogOpen(false);
    pendingLeaveActionRef.current = null;
    leaveAction?.();
  }, [bumpDirty]);

  const discardPendingNavigation = useCallback(() => {
    const navigateFn = pendingNavigationRef.current;
    dirtySectionsRef.current.delete(activeSectionId);
    bumpDirty();
    setDialogOpen(false);
    setPendingSectionId(null);
    pendingNavigationRef.current = null;
    navigateFn?.();
  }, [activeSectionId, bumpDirty]);

  const pauseDirtyTracking = useCallback((durationMs = 1200) => {
    suppressDirtyRef.current = true;
    window.setTimeout(() => {
      if (!isLoading) {
        suppressDirtyRef.current = false;
      }
    }, durationMs);
  }, [isLoading]);

  const getSectionCaptureProps = useCallback(
    (sectionId) => ({
      onChangeCapture: (event) => {
        // Run after the field's own onChange — sync capture was swallowing the first keystroke.
        const mark = () => markSectionDirty(sectionId);
        if (isEditableElement(event.target)) {
          queueMicrotask(mark);
        } else {
          mark();
        }
      },
    }),
    [markSectionDirty]
  );

  const enhanceSectionChild = useCallback(
    (sectionId, children) => {
      if (!isValidElement(children)) return children;
      const onUserEdit = () => markSectionDirty(sectionId);
      return cloneElement(children, {
        onUserEdit: (...args) => {
          onUserEdit();
          children.props.onUserEdit?.(...args);
        },
      });
    },
    [markSectionDirty]
  );

  const suppressDirtyTracking = useCallback((fn) => {
    suppressDirtyRef.current = true;
    try {
      return fn();
    } finally {
      suppressDirtyRef.current = false;
    }
  }, []);

  return {
    dialogOpen,
    leaveDialogOpen,
    pendingSectionId,
    isSectionDirty,
    isActiveSectionDirty,
    markSectionDirty,
    markActiveSectionDirty,
    clearSectionDirty,
    requestSectionNavigation,
    cancelPendingNavigation,
    completePendingNavigation,
    requestLeave,
    cancelLeave,
    confirmLeaveSave,
    discardLeave,
    discardPendingNavigation,
    pauseDirtyTracking,
    getSectionCaptureProps,
    enhanceSectionChild,
    suppressDirtyTracking,
  };
}
